import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { MRT_ColumnDef, MRT_RowSelectionState } from 'material-react-table'
import { ReassignUser, TierUser } from 'types/organization'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_ENDPOINTS as API } from 'services/endpoints'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { ArrowRightAlt as ArrowRightAltIcon } from '@mui/icons-material'
import Avatar from 'components/Avatar'
import { EmploymentType } from 'types/organization'
import { SelectOption } from 'types/common'
import { baseApiClient } from 'services/axiosConfig'
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar'
import { useEmploymentTypeDetails } from 'services/api/organization'
import { useEmploymentTypes } from 'services/api/organization'
import KeeprTable from 'components/KeeprTable'

interface ReassignEmploymentTypeDialogProps {
  open: boolean
  handleClose: () => void
  employmentType: EmploymentType | null
}

const ReassignEmploymentTypeDialog = ({
  open,
  handleClose,
  employmentType,
}: ReassignEmploymentTypeDialogProps) => {
  const queryClient = useQueryClient()
  const enqueueSnackbar = useEnqueueSnackbar()

  const [groupSelection, setGroupSelection] = useState<number | string>('')
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})

  const { data: employmentTypeDetails = null } = useEmploymentTypeDetails({
    employmentTypeId: employmentType?.id || '',
    options: {
      placeholderData: (previousData) => previousData,
      enabled: Boolean(employmentType?.id),
    },
  })

  const { data: employmentTypes = [] } = useEmploymentTypes<SelectOption[]>({
    options: {
      select: (tiers) =>
        tiers
          .map((item) => {
            return {
              label: item.name,
              value: item.id,
            }
          })
          .filter((item) => item.label !== employmentTypeDetails?.name),
    },
  })

  const { mutate: reassignUsersEmploymentType } = useMutation<unknown, unknown, ReassignUser>({
    mutationFn: (data) =>
      baseApiClient.patch(API.org.reassignEmploymentType(), data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employment-types'] })
    },
    onError: () => {
      enqueueSnackbar('Something went wrong. Please try Again.', { variant: 'error' })
    },
  })

  useEffect(() => {
    if (employmentTypeDetails?.jobs_count !== 0) {
      return
    }

    handleClose()
  }, [handleClose, employmentTypeDetails])

  const handleChangeUsersEmploymentType = () => {
    reassignUsersEmploymentType({
      values: Object.keys(rowSelection).map((userUuid) => {
        return {
          user_uuid: userUuid,
          obj_id: Number(groupSelection),
        }
      }),
    })
  }

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: 'full_name',
          header: 'User',
          size: 150,
          Cell: ({ cell, row }) => {
            const fullName = cell.getValue<string>()
            return (
              <Stack direction='row' alignItems='center' gap={1}>
                <Avatar name={fullName} src={row.original.avatar_thumbnail} size={40} />
                <Stack overflow='hidden'>
                  <Typography variant='body2' noWrap={true}>
                    {fullName}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' noWrap={true}>
                    {row.original.email}
                  </Typography>
                </Stack>
              </Stack>
            )
          },
        },
        {
          id: 'arrow',
          header: null,
          size: 50,
          muiTableBodyCellProps: {
            align: 'center',
          },
          Cell: () => {
            return <ArrowRightAltIcon sx={{ fontSize: '24px', verticalAlign: 'middle' }} />
          },
        },
        {
          id: 'reassign',
          header: 'Reassign',
          size: 150,
          Cell: ({ row }) => {
            return (
              <Select
                size='small'
                fullWidth
                displayEmpty
                defaultValue=''
                onChange={(event) => {
                  reassignUsersEmploymentType({
                    values: [
                      {
                        user_uuid: row.original.uuid,
                        obj_id: Number(event.target.value),
                      },
                    ],
                  })
                }}
              >
                <MenuItem disabled value=''>
                  Select type
                </MenuItem>
                {employmentTypes.map((employmentType) => (
                  <MenuItem key={employmentType.value} value={employmentType.value}>
                    {employmentType.label}
                  </MenuItem>
                ))}
              </Select>
            )
          },
        },
      ] as MRT_ColumnDef<TierUser>[],
    [reassignUsersEmploymentType, employmentTypes],
  )

  const data = useMemo(() => {
    if (!employmentTypeDetails) {
      return []
    }

    const usersList = employmentTypeDetails.userjob_set.map((item) => item.user)
    return usersList
  }, [employmentTypeDetails])

  const numbOfSelectedRows = Object.keys(rowSelection).length

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby='Reassign employment type'
      open={open}
      fullWidth={true}
      data-testid='reassign-employment-type-dialog'
    >
      <DialogTitle>
        <Link
          component='button'
          variant='h6'
          color='text.primary'
          onClick={handleClose}
          underline='none'
          type='button'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '20px' }} />
          <span>Reassign users to a new employment type</span>
        </Link>
        <Typography variant='body2' color='text.secondary'>
          The {employmentType?.name} employment type you want to remove currently has{' '}
          {employmentType?.jobs_count} {employmentType?.jobs_count === 1 ? 'user' : 'users'}.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1, '&.MuiDialogContent-root': { pt: 1 } }}>
        {numbOfSelectedRows > 0 ? (
          <>
            <Stack direction='row' justifyContent='space-between' alignItems='center' pb={1}>
              <Typography variant='body2' color='text.secondary' pl={2}>
                {numbOfSelectedRows} selected
              </Typography>

              <Box>
                <Select
                  size='small'
                  sx={{ width: '180px', mr: 2 }}
                  displayEmpty
                  value={groupSelection}
                  onChange={(event) => setGroupSelection(event.target.value)}
                >
                  <MenuItem disabled value='' data-testid='select-employment-type'>
                    Select type
                  </MenuItem>
                  {employmentTypes.map((employmentType) => (
                    <MenuItem key={employmentType.value} value={employmentType.value}>
                      {employmentType.label}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  variant='text'
                  onClick={handleChangeUsersEmploymentType}
                  disabled={!groupSelection}
                >
                  Apply
                </Button>
              </Box>
            </Stack>
            <Divider />
          </>
        ) : null}

        <KeeprTable
          columns={columns}
          data={data}
          rowCount={data.length}
          enableRowSelection={true}
          enablePagination={false}
          getRowId={(row) => row.uuid}
          onRowSelectionChange={setRowSelection}
          state={{ rowSelection }}
          muiTablePaperProps={{
            elevation: 0,
          }}
          muiTableHeadCellProps={{
            sx: {
              '&.MuiTableCell-root:first-of-type': {
                width: '30px',
              },
            },
          }}
          muiTableBodyProps={{
            title: 'Reassign users - employment types',
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant='outlined' onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
export default ReassignEmploymentTypeDialog
